import React, { useState, useEffect } from 'react'
import Header from '../../layouts/Header'
import { Button, Card, Table, Badge, Modal, Form, Alert } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Select from 'react-select'
import axiosInstance from '../../api/axios'

const VendorAllocation = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : ""
    const [skin, setSkin] = useState(currentSkin)
    const { access } = useSelector((state) => state.login)
    const { loginEmail } = useSelector((state) => state.login);
    const email = loginEmail || localStorage.getItem("email");
    const [vendorAccess, setVendorAccess] = useState([])

    const accessToken = localStorage.getItem("authToken")
    const navigate = useNavigate()
    const [vendors, setVendors] = useState([])
    const [allocatedVendors, setAllocatedVendors] = useState([])
    const [outlets, setOutlets] = useState([])

    // Modal states
    const [showAllocateModal, setShowAllocateModal] = useState(false)
    const [showUpdateModal, setShowUpdateModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [selectedAllocation, setSelectedAllocation] = useState(null)

    // Form state
    const [allocationForm, setAllocationForm] = useState({
        outlet: null,
        vendor: null
    })
    const [updateForm, setUpdateForm] = useState({
        _id: '',
        outlet: null,
        vendor: null
    })
    const [loading, setLoading] = useState(false)
    const [formErrors, setFormErrors] = useState({})
    const [updateFormErrors, setUpdateFormErrors] = useState({})

    // Filter access permissions
    useEffect(() => {
        if (Array.isArray(access)) {
            const filteredAccess = access?.filter((perm) =>
                perm?.startsWith("Vendors:")
            )
            setVendorAccess(filteredAccess)
        }
    }, [access])

    // Fetch data
    const getAllocatedVendors = async () => {
        try {
            setLoading(true)
            const headers = { Authorization: `Bearer ${accessToken}` }
            const response = await axiosInstance.get("local-outlet-vendor-allocation", { headers })

            if (response?.data?.status === true) {
                const dataWithSerial = response?.data?.data?.dbResponse.map((item, index) => ({
                    ...item,
                    serialNumber: index + 1
                }))
                setAllocatedVendors(dataWithSerial)
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch allocated vendors")
        } finally {
            setLoading(false)
        }
    }

    const getVendors = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` }
            const response = await axiosInstance.get("vendor", { headers })

            if (response?.data?.status === true) {
                const vendorsData = response?.data?.data?.vendors.map((vendor) => ({
                    value: vendor._id,
                    label: `${vendor.name}${vendor.gstin ? ` - ${vendor.gstin}` : ''}`,
                    ...vendor
                }))
                setVendors(vendorsData)
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch vendors")
        }
    }

    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` }
            const response = await axiosInstance.get("outlet", { headers })

            if (response?.data?.status === true) {
                const outletsData = response?.data?.data?.outlets.map((outlet) => ({
                    value: outlet?._id,
                    label: `${outlet?.name} - ${outlet?.city}, ${outlet?.state}`,
                    ...outlet
                }))
                setOutlets(outletsData)
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch outlets")
        }
    }

    const handleApiError = (error, defaultMessage) => {
        if (error.response && error.response?.data?.status === false) {
            let errorMessage = error.response.data.error
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            })
        } else if (error.response && error.response?.data?.status === 404) {
            let errorMessage = error.response.data.error
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            })
        } else {
            toast.error(defaultMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            })
        }
    }

    // Handle allocation
    const handleAllocateVendor = async (e) => {
        e.preventDefault()

        // Validate form
        const errors = {}
        if (!allocationForm.outlet) errors.outlet = "Outlet is required"
        if (!allocationForm.vendor) errors.vendor = "Vendor is required"

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors)
            return
        }

        if (isAlreadyAllocated(allocationForm.outlet.value, allocationForm.vendor.value)) {
            toast.error("This vendor is already allocated to the selected outlet", {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            })
            return
        }

        try {
            setLoading(true)
            const headers = { Authorization: `Bearer ${accessToken}` }
            const payload = {
                outlet: allocationForm.outlet.value,
                vendor: allocationForm.vendor.value
            }

            const response = await axiosInstance.post("local-outlet-vendor-allocation", payload, { headers })

            if (response?.data?.status === true) {
                toast.success("Vendor allocated successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                })
                setShowAllocateModal(false)
                setAllocationForm({ outlet: null, vendor: null })
                setFormErrors({})
                getAllocatedVendors()
            }
        } catch (error) {
            handleApiError(error, "Failed to allocate vendor")
        } finally {
            setLoading(false)
        }
    }

    // Handle update
    const handleUpdateAllocation = async (e) => {
        e.preventDefault()

        // Validate form
        const errors = {}
        if (!updateForm.outlet) errors.outlet = "Outlet is required"
        if (!updateForm.vendor) errors.vendor = "Vendor is required"

        if (Object.keys(errors).length > 0) {
            setUpdateFormErrors(errors)
            return
        }

        // Check if the same allocation already exists (excluding current one)
        if (isAlreadyAllocatedForUpdate(updateForm.outlet.value, updateForm.vendor.value, updateForm._id)) {
            toast.error("This vendor is already allocated to the selected outlet", {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            })
            return
        }

        try {
            setLoading(true)
            const headers = { Authorization: `Bearer ${accessToken}` }
            const payload = {
                _id: updateForm._id,
                outlet: updateForm.outlet.value,
                vendor: updateForm.vendor.value
            }

            const response = await axiosInstance.put("local-outlet-vendor-allocation", payload, { headers });

            if (response?.data?.status === true) {
                toast.success("Vendor allocation updated successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                })
                setShowUpdateModal(false)
                setUpdateForm({ _id: '', outlet: null, vendor: null })
                setUpdateFormErrors({})
                getAllocatedVendors()
            }
        } catch (error) {
            handleApiError(error, "Failed to update vendor allocation")
        } finally {
            setLoading(false)
        }
    }

    // Handle delete
    const handleDeleteAllocation = async () => {
        try {
            setLoading(true)
            const headers = { 
                Authorization: `Bearer ${accessToken}` 
            }
            let reqBody = {
                _id: selectedAllocation._id
            }
            
            const response = await axiosInstance.delete(`local-outlet-vendor-allocation`, { headers, data: reqBody })

            if (response?.data?.status === true) {
                toast.success("Allocation deleted successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                })
                setShowDeleteModal(false)
                setSelectedAllocation(null)
                getAllocatedVendors()
            }
        } catch (error) {
            handleApiError(error, "Failed to delete allocation")
        } finally {
            setLoading(false)
        }
    }

    // Open modals
    const openAllocateModal = () => {
        setAllocationForm({ outlet: null, vendor: null })
        setFormErrors({})
        setShowAllocateModal(true)
    }

    const openUpdateModal = (allocation) => {
        const selectedOutlet = outlets.find(outlet => outlet.value === allocation.outlet._id);
        const selectedVendor = vendors.find(vendor => vendor.value === allocation.vendor._id);
        
        setUpdateForm({
            _id: allocation._id,
            outlet: selectedOutlet,
            vendor: selectedVendor
        })
        setUpdateFormErrors({})
        setShowUpdateModal(true)
    }

    const openDeleteModal = (allocation) => {
        setSelectedAllocation(allocation)
        setShowDeleteModal(true)
    }

    // Check if vendor is already allocated to outlet (for create)
    const isAlreadyAllocated = (outletId, vendorId) => {
        return allocatedVendors.some(allocation =>
            allocation.outlet._id === outletId && allocation.vendor._id === vendorId
        )
    }

    // Check if vendor is already allocated to outlet (for update - excluding current allocation)
    const isAlreadyAllocatedForUpdate = (outletId, vendorId, currentAllocationId) => {
        return allocatedVendors.some(allocation =>
            allocation.outlet._id === outletId && 
            allocation.vendor._id === vendorId && 
            allocation._id !== currentAllocationId
        )
    }

    // Get available vendors for selected outlet (for create)
    const getAvailableVendors = () => {
        if (!allocationForm.outlet) return vendors
        return vendors.filter(vendor =>
            !isAlreadyAllocated(allocationForm.outlet.value, vendor.value)
        )
    }

    // Get available vendors for selected outlet (for update)
    const getAvailableVendorsForUpdate = () => {
        if (!updateForm.outlet) return vendors
        return vendors.filter(vendor =>
            !isAlreadyAllocatedForUpdate(updateForm.outlet.value, vendor.value, updateForm._id)
        )
    }

    // Custom styles for react-select with smaller fonts
    const customStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: formErrors.outlet || formErrors.vendor || updateFormErrors.outlet || updateFormErrors.vendor ? '#dc3545' : '#dee2e6',
            borderRadius: '0.375rem',
            minHeight: '36px',
            fontSize: '0.875rem',
            '&:hover': {
                borderColor: formErrors.outlet || formErrors.vendor || updateFormErrors.outlet || updateFormErrors.vendor ? '#dc3545' : '#86b7fe'
            }
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999,
            fontSize: '0.875rem'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e9ecef' : 'white',
            color: state.isSelected ? 'white' : 'black',
            fontSize: '0.875rem',
            padding: '0.5rem 1rem',
            '&:active': {
                backgroundColor: '#0d6efd'
            }
        }),
        input: (base) => ({
            ...base,
            fontSize: '0.875rem'
        }),
        placeholder: (base) => ({
            ...base,
            fontSize: '0.875rem'
        }),
        singleValue: (base) => ({
            ...base,
            fontSize: '0.875rem'
        })
    }

    // Theme for react-select based on skin mode
    const getSelectTheme = (theme) => ({
        ...theme,
        colors: {
            ...theme.colors,
            primary: '#0d6efd',
            primary25: skin === 'dark' ? '#2b3035' : '#e9ecef',
            neutral0: skin === 'dark' ? '#212529' : 'white',
            neutral80: skin === 'dark' ? 'white' : 'black',
        }
    })

    useEffect(() => {
        getAllocatedVendors()
        getVendors()
        getOutlets()
    }, [])

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`} style={{ fontSize: '0.875rem' }}>

                {/* Action Bar */}
                <div className="row">
                    <div className="col-12">
                        <div className={`card shadow-sm border-0 ${skin === "dark" ? "bg-dark" : "bg-white"}`}>
                            <div className="card-body py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="mb-0 fw-semibold" style={{ fontSize: '1rem' }}>
                                            <i className="bi bi-list-check me-2 text-primary"></i>
                                            VENDOR ALLOCATION MANAGEMENT
                                        </h6>
                                    </div>
                                    <div className="d-flex gap-3">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => navigate('/masters/vendors')}
                                            className="d-flex align-items-center"
                                            size="sm"
                                            style={{ fontSize: '0.813rem' }}
                                        >
                                            <i className="bi bi-arrow-left me-2"></i>
                                            Back to Vendors
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={openAllocateModal}
                                            disabled={loading}
                                            className="d-flex align-items-center shadow-sm"
                                            size="sm"
                                            style={{ fontSize: '0.813rem' }}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Allocate Vendor
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Allocations Table */}
                <div className="row">
                    <div className="col-12">
                        <div className={`card shadow-sm border-0 ${skin === "dark" ? "bg-dark" : "bg-white"}`}>
                            <div className="card-body p-0">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" style={{ width: '2.5rem', height: '2.5rem' }} role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3 text-muted" style={{ fontSize: '0.875rem' }}>Loading vendor allocations...</p>
                                    </div>
                                ) : allocatedVendors.length === 0 ? (
                                    <div className="text-center py-5">
                                        <div className={`rounded-circle p-4 d-inline-block ${skin === "dark" ? "bg-dark" : "bg-light"}`}>
                                            <i className="bi bi-inbox display-4 text-muted"></i>
                                        </div>
                                        <h5 className="mt-4 text-muted" style={{ fontSize: '1.125rem' }}>No Allocations Found</h5>
                                        <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>Get started by allocating your first vendor to an outlet</p>
                                        <Button
                                            variant="primary"
                                            onClick={openAllocateModal}
                                            size="sm"
                                            className="px-4"
                                            style={{ fontSize: '0.813rem' }}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Create First Allocation
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table className="mb-0 table-hover" bordered size="sm">
                                            <thead className={`${skin === "dark" ? "bg-dark-secondary" : "bg-light"}`}>
                                                <tr>
                                                    <th className="ps-4 py-2 fw-semibold" width="60" style={{ fontSize: '0.813rem' }}>Sr.no</th>
                                                    <th className="py-2 fw-semibold" style={{ fontSize: '0.813rem' }}>Outlet Details</th>
                                                    <th className="py-2 fw-semibold" style={{ fontSize: '0.813rem' }}>Vendor Details</th>
                                                    <th className="py-2 fw-semibold" style={{ fontSize: '0.813rem' }}>Contact Information</th>
                                                    <th className="py-2 fw-semibold" style={{ fontSize: '0.813rem' }}>Allocation Date</th>
                                                    <th className="text-center py-2 fw-semibold" width="120" style={{ fontSize: '0.813rem' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {allocatedVendors.map((allocation) => (
                                                    <tr key={allocation._id} className={skin === "dark" ? "border-dark" : ""}>
                                                        <td className="ps-4 align-middle" style={{ fontSize: '0.813rem' }}>
                                                            <div className="fw-semibold text-muted">{allocation.serialNumber}</div>
                                                        </td>
                                                        <td className="align-middle">
                                                            <div className="d-flex align-items-center">
                                                                <div className={`rounded-circle p-1 me-3 ${skin === "dark" ? "bg-dark-secondary" : "bg-light"}`}>
                                                                    <i className="bi bi-shop text-primary" style={{ fontSize: '0.875rem' }}></i>
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-1 fw-semibold" style={{ fontSize: '0.875rem' }}>{allocation.outlet.name}</h6>
                                                                    <div className="d-flex flex-wrap gap-2">
                                                                        <Badge bg="outline-primary text-muted" className="fs-10" style={{ fontSize: '0.688rem' }}>
                                                                            {allocation.outlet.city}, {allocation.outlet.state} 
                                                                        </Badge>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="align-middle">
                                                            <div className="d-flex align-items-center">
                                                                <div className={`rounded-circle p-1 me-3 ${skin === "dark" ? "bg-dark-secondary" : "bg-light"}`}>
                                                                    <i className="bi bi-building text-success" style={{ fontSize: '0.875rem' }}></i>
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-1 fw-semibold" style={{ fontSize: '0.875rem' }}>{allocation.vendor.name}</h6>
                                                                    {allocation.vendor.gstin && (
                                                                        <Badge bg="outline-danger text-muted" className="fs-10" style={{ fontSize: '0.688rem' }}>
                                                                            GST: {allocation.vendor.gstin}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="align-middle">
                                                            <div className="d-flex flex-column gap-1">
                                                                {allocation.vendor.email && (
                                                                    <div className="d-flex align-items-center text-muted">
                                                                        <i className="bi bi-envelope me-2" style={{ fontSize: '0.75rem' }}></i>
                                                                        <small style={{ fontSize: '0.75rem' }}>{allocation.vendor.email}</small>
                                                                    </div>
                                                                )}
                                                                {allocation.vendor.phone && (
                                                                    <div className="d-flex align-items-center text-muted">
                                                                        <i className="bi bi-phone me-2" style={{ fontSize: '0.75rem' }}></i>
                                                                        <small style={{ fontSize: '0.75rem' }}>{allocation.vendor.phone}</small>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="align-middle">
                                                            <div className="d-flex flex-column">
                                                                <span className="fw-semibold" style={{ fontSize: '0.813rem' }}>
                                                                    {new Date(allocation.createdAt).toLocaleDateString()}
                                                                </span>
                                                                <small className="text-muted" style={{ fontSize: '0.688rem' }}>
                                                                    {new Date(allocation.createdAt).toLocaleTimeString()}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td className="text-center align-middle">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => openUpdateModal(allocation)}
                                                                title="Update Allocation"
                                                                className="rounded-circle me-2"
                                                                style={{ width: '32px', height: '32px', padding: '0', fontSize: '0.75rem' }}
                                                            >
                                                                <i className="bi bi-pencil"></i>
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => openDeleteModal(allocation)}
                                                                title="Remove Allocation"
                                                                disabled={loading}
                                                                className="rounded-circle"
                                                                style={{ width: '32px', height: '32px', padding: '0', fontSize: '0.75rem' }}
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Allocate Vendor Modal */}
                <Modal show={showAllocateModal} onHide={() => setShowAllocateModal(false)} centered size="lg">
                    <Modal.Header closeButton className={`border-0 ${skin === "dark" ? "bg-dark text-light" : "bg-white"}`}>
                        <Modal.Title className="fw-bold" style={{ fontSize: '1.125rem' }}>
                            <i className="bi bi-link-45deg me-2 text-primary"></i>
                            Allocate Vendor
                        </Modal.Title>
                    </Modal.Header>
                    <div className={skin === "dark" ? "bg-dark text-light" : "bg-white"} style={{ fontSize: '0.875rem' }}>
                        <Form onSubmit={handleAllocateVendor}>
                            <Modal.Body className="p-1">
                                <div className="row">
                                    <div className="col-12">
                                        <div className={`p-4 rounded-3 ${skin === "dark" ? "bg-dark-secondary" : "bg-light"}`}>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="fw-semibold mb-3" style={{ fontSize: '0.875rem' }}>
                                                    <i className="bi bi-shop me-2 text-primary"></i>
                                                    Select Outlet <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Select
                                                    value={allocationForm.outlet}
                                                    onChange={(selectedOption) => {
                                                        setAllocationForm({
                                                            outlet: selectedOption,
                                                            vendor: null
                                                        })
                                                        setFormErrors({ ...formErrors, outlet: null })
                                                    }}
                                                    options={outlets}
                                                    placeholder="Search for an outlet..."
                                                    isSearchable
                                                    isClearable
                                                    styles={customStyles}
                                                    theme={getSelectTheme}
                                                    noOptionsMessage={() => "No outlets found"}
                                                    className={formErrors.outlet ? 'is-invalid' : ''}
                                                />
                                                {formErrors.outlet && (
                                                    <div className="text-danger small mt-2" style={{ fontSize: '0.75rem' }}>
                                                        <i className="bi bi-exclamation-circle me-1"></i>
                                                        {formErrors.outlet}
                                                    </div>
                                                )}
                                            </Form.Group>

                                            <Form.Group className="mb-0">
                                                <Form.Label className="fw-semibold mb-3" style={{ fontSize: '0.875rem' }}>
                                                    <i className="bi bi-building me-2 text-success"></i>
                                                    Select Vendor <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Select
                                                    value={allocationForm.vendor}
                                                    onChange={(selectedOption) => {
                                                        setAllocationForm({ ...allocationForm, vendor: selectedOption })
                                                        setFormErrors({ ...formErrors, vendor: null })
                                                    }}
                                                    options={getAvailableVendors()}
                                                    placeholder={allocationForm.outlet ? "Search for a vendor..." : "Please select an outlet first"}
                                                    isSearchable
                                                    isClearable
                                                    isDisabled={!allocationForm.outlet}
                                                    styles={customStyles}
                                                    theme={getSelectTheme}
                                                    noOptionsMessage={() =>
                                                        allocationForm.outlet ? "No available vendors found" : "Please select an outlet first"
                                                    }
                                                    className={formErrors.vendor ? 'is-invalid' : ''}
                                                />
                                                {formErrors.vendor && (
                                                    <div className="text-danger small mt-2" style={{ fontSize: '0.75rem' }}>
                                                        <i className="bi bi-exclamation-circle me-1"></i>
                                                        {formErrors.vendor}
                                                    </div>
                                                )}
                                                <div className="mt-3">
                                                    {allocationForm.outlet && (
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                <i className="bi bi-info-circle me-1"></i>
                                                                {getAvailableVendors().length} vendors available for allocation
                                                            </small>
                                                            <Badge bg="outline-primary" className="fs-11" style={{ fontSize: '0.688rem' }}>
                                                                {allocationForm.outlet.label}
                                                            </Badge>
                                                        </div>
                                                    )}
                                                </div>
                                            </Form.Group>
                                        </div>

                                        {allocationForm.outlet && allocationForm.vendor &&
                                            isAlreadyAllocated(allocationForm.outlet.value, allocationForm.vendor.value) && (
                                                <Alert variant="warning" className="mt-4 border-0 d-flex align-items-center" style={{ fontSize: '0.875rem' }}>
                                                    <i className="bi bi-exclamation-triangle me-3" style={{ fontSize: '1.25rem' }}></i>
                                                    <div>
                                                        <h6 className="alert-heading mb-1" style={{ fontSize: '0.938rem' }}>Already Allocated</h6>
                                                        <p className="mb-0" style={{ fontSize: '0.813rem' }}>This vendor is already allocated to the selected outlet. Please choose a different vendor.</p>
                                                    </div>
                                                </Alert>
                                            )}
                                    </div>
                                </div>
                            </Modal.Body>
                            <Modal.Footer className="border-0 pt-0">
                                <div className="d-flex gap-3 w-100">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setShowAllocateModal(false)}
                                        disabled={loading}
                                        className="flex-fill"
                                        size="sm"
                                        style={{ fontSize: '0.813rem' }}
                                    >
                                        <i className="bi bi-x-circle me-2"></i>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={loading ||
                                            !allocationForm.outlet ||
                                            !allocationForm.vendor ||
                                            isAlreadyAllocated(allocationForm.outlet.value, allocationForm.vendor.value)
                                        }
                                        className="flex-fill shadow-sm"
                                        size="sm"
                                        style={{ fontSize: '0.813rem' }}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Allocating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-circle me-2"></i>
                                                Confirm Allocation
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Modal.Footer>
                        </Form>
                    </div>
                </Modal>

                {/* Update Vendor Allocation Modal */}
                <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} centered size="lg">
                    <Modal.Header closeButton className={`border-0 ${skin === "dark" ? "bg-dark text-light" : "bg-white"}`}>
                        <Modal.Title className="fw-bold" style={{ fontSize: '1.125rem' }}>
                            <i className="bi bi-pencil-square me-2 text-warning"></i>
                            Update Vendor Allocation
                        </Modal.Title>
                    </Modal.Header>
                    <div className={skin === "dark" ? "bg-dark text-light" : "bg-white"} style={{ fontSize: '0.875rem' }}>
                        <Form onSubmit={handleUpdateAllocation}>
                            <Modal.Body className="p-1">
                                <div className="row">
                                    <div className="col-12">
                                        <div className={`p-4 rounded-3 ${skin === "dark" ? "bg-dark-secondary" : "bg-light"}`}>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="fw-semibold mb-3" style={{ fontSize: '0.875rem' }}>
                                                    <i className="bi bi-shop me-2 text-primary"></i>
                                                    Select Outlet <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Select
                                                    value={updateForm.outlet}
                                                    onChange={(selectedOption) => {
                                                        setUpdateForm({
                                                            ...updateForm,
                                                            outlet: selectedOption,
                                                            vendor: null // Reset vendor when outlet changes
                                                        })
                                                        setUpdateFormErrors({ ...updateFormErrors, outlet: null })
                                                    }}
                                                    options={outlets}
                                                    placeholder="Search for an outlet..."
                                                    isSearchable
                                                    isClearable
                                                    styles={customStyles}
                                                    theme={getSelectTheme}
                                                    noOptionsMessage={() => "No outlets found"}
                                                    className={updateFormErrors.outlet ? 'is-invalid' : ''}
                                                />
                                                {updateFormErrors.outlet && (
                                                    <div className="text-danger small mt-2" style={{ fontSize: '0.75rem' }}>
                                                        <i className="bi bi-exclamation-circle me-1"></i>
                                                        {updateFormErrors.outlet}
                                                    </div>
                                                )}
                                            </Form.Group>

                                            <Form.Group className="mb-0">
                                                <Form.Label className="fw-semibold mb-3" style={{ fontSize: '0.875rem' }}>
                                                    <i className="bi bi-building me-2 text-success"></i>
                                                    Select Vendor <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Select
                                                    value={updateForm.vendor}
                                                    onChange={(selectedOption) => {
                                                        setUpdateForm({ ...updateForm, vendor: selectedOption })
                                                        setUpdateFormErrors({ ...updateFormErrors, vendor: null })
                                                    }}
                                                    options={getAvailableVendorsForUpdate()}
                                                    placeholder={updateForm.outlet ? "Search for a vendor..." : "Please select an outlet first"}
                                                    isSearchable
                                                    isClearable
                                                    isDisabled={!updateForm.outlet}
                                                    styles={customStyles}
                                                    theme={getSelectTheme}
                                                    noOptionsMessage={() =>
                                                        updateForm.outlet ? "No available vendors found" : "Please select an outlet first"
                                                    }
                                                    className={updateFormErrors.vendor ? 'is-invalid' : ''}
                                                />
                                                {updateFormErrors.vendor && (
                                                    <div className="text-danger small mt-2" style={{ fontSize: '0.75rem' }}>
                                                        <i className="bi bi-exclamation-circle me-1"></i>
                                                        {updateFormErrors.vendor}
                                                    </div>
                                                )}
                                                <div className="mt-3">
                                                    {updateForm.outlet && (
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                <i className="bi bi-info-circle me-1"></i>
                                                                {getAvailableVendorsForUpdate().length} vendors available for allocation
                                                            </small>
                                                            <Badge bg="outline-primary" className="fs-11" style={{ fontSize: '0.688rem' }}>
                                                                {updateForm.outlet.label}
                                                            </Badge>
                                                        </div>
                                                    )}
                                                </div>
                                            </Form.Group>
                                        </div>

                                        {updateForm.outlet && updateForm.vendor &&
                                            isAlreadyAllocatedForUpdate(updateForm.outlet.value, updateForm.vendor.value, updateForm._id) && (
                                                <Alert variant="warning" className="mt-4 border-0 d-flex align-items-center" style={{ fontSize: '0.875rem' }}>
                                                    <i className="bi bi-exclamation-triangle me-3" style={{ fontSize: '1.25rem' }}></i>
                                                    <div>
                                                        <h6 className="alert-heading mb-1" style={{ fontSize: '0.938rem' }}>Already Allocated</h6>
                                                        <p className="mb-0" style={{ fontSize: '0.813rem' }}>This vendor is already allocated to the selected outlet. Please choose a different vendor.</p>
                                                    </div>
                                                </Alert>
                                            )}
                                    </div>
                                </div>
                            </Modal.Body>
                            <Modal.Footer className="border-0 pt-0">
                                <div className="d-flex gap-3 w-100">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setShowUpdateModal(false)}
                                        disabled={loading}
                                        className="flex-fill"
                                        size="sm"
                                        style={{ fontSize: '0.813rem' }}
                                    >
                                        <i className="bi bi-x-circle me-2"></i>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={loading ||
                                            !updateForm.outlet ||
                                            !updateForm.vendor ||
                                            isAlreadyAllocatedForUpdate(updateForm.outlet.value, updateForm.vendor.value, updateForm._id)
                                        }
                                        className="flex-fill shadow-sm text-white"
                                        size="sm"
                                        style={{ fontSize: '0.813rem' }}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-circle me-2"></i>
                                                Update Allocation
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Modal.Footer>
                        </Form>
                    </div>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton className={`border-0 ${skin === "dark" ? "bg-dark text-light" : "bg-white"}`}>
                        <Modal.Title className="fw-bold text-danger" style={{ fontSize: '1.125rem' }}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            Confirm Removal
                        </Modal.Title>
                    </Modal.Header>
                    <div className={skin === "dark" ? "bg-dark text-light" : "bg-white"} style={{ fontSize: '0.875rem' }}>
                        <Modal.Body className="p-4 text-center">
                            {selectedAllocation && (
                                <div className="py-3">
                                    <div className={`rounded-circle p-3 d-inline-flex mb-4 ${skin === "dark" ? "bg-dark-secondary" : "bg-light-danger"}`}>
                                        <i className="bi bi-trash text-danger" style={{ fontSize: '1.5rem' }}></i>
                                    </div>
                                    <h5 className="fw-bold mb-3" style={{ fontSize: '1rem' }}>Remove Allocation?</h5>
                                    <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
                                        This will permanently remove the vendor allocation. This action cannot be undone.
                                    </p>

                                    <div className={`rounded-3 p-4 mb-4 ${skin === "dark" ? "bg-dark-secondary" : "bg-light"}`} style={{ fontSize: '0.875rem' }}>
                                        <div className="row text-start">
                                            <div className="col-6">
                                                <h6 className="fw-semibold mb-2" style={{ fontSize: '0.875rem' }}>Outlet Details</h6>
                                                <div className="d-flex align-items-center mb-3">
                                                    <i className="bi bi-shop me-2 text-primary" style={{ fontSize: '0.875rem' }}></i>
                                                    <span style={{ fontSize: '0.813rem' }}>{selectedAllocation.outlet.name}</span>
                                                </div>
                                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                    {selectedAllocation.outlet.city}, {selectedAllocation.outlet.state}
                                                </small>
                                            </div>
                                            <div className="col-6">
                                                <h6 className="fw-semibold mb-2" style={{ fontSize: '0.875rem' }}>Vendor Details</h6>
                                                <div className="d-flex align-items-center mb-3">
                                                    <i className="bi bi-building me-2 text-success" style={{ fontSize: '0.875rem' }}></i>
                                                    <span style={{ fontSize: '0.813rem' }}>{selectedAllocation.vendor.name}</span>
                                                </div>
                                                {selectedAllocation.vendor.gstin && (
                                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        GST: {selectedAllocation.vendor.gstin}
                                                    </small>
                                                )}
                                                </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer className="border-0">
                            <div className="d-flex gap-3 w-100">
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setShowDeleteModal(false)}
                                    disabled={loading}
                                    className="flex-fill"
                                    size="sm"
                                    style={{ fontSize: '0.813rem' }}
                                >
                                    <i className="bi bi-arrow-left me-2"></i>
                                    Keep Allocation
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={handleDeleteAllocation}
                                    disabled={loading}
                                    className="flex-fill"
                                    size="sm"
                                    style={{ fontSize: '0.813rem' }}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Removing...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-trash me-2"></i>
                                            Remove Allocation
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Modal.Footer>
                    </div>
                </Modal>
            </div>
        </React.Fragment>
    )
}

export default VendorAllocation